/**
 *  @swagger
 *  definitions:
 *    RegattaResultTableParams:
 *      type: object
 *      properties:
 *        RegattaId:
 *          description: ID of the associated Regatta
 *          type: string
 *        RegattaGroupId:
 *          description: ID of the associated RegattaGroup
 *          type: string
 *        Name:
 *          description: Name of the RegattaResultTable
 *          type: string
 *        Type:
 *          description: Type of the RegattaResultTableRace
 *          $ref: '#/components/enums/RegattaResultTableType'
 *        Discards:
 *          description: Discards of the RegattaResultTable
 *          type: integer
 *        StartGroup:
 *          description: StartGroup of the RegattaResultTable
 *          type: integer
 *
 *  @swagger
 *  definitions:
 *    RegattaResultTableCreateParams:
 *      allOf:
 *        - $ref: '#/definitions/RegattaResultTableParams'
 *        - type: object
 *          required:
 *            - RegattaId
 *            - RegattaGroupId
 *            - Name
 *            - Type
 *            - Discards
 *
 *  @swagger
 *  definitions:
 *    RegattaResultTableQueryParams:
 *      allOf:
 *        - type: object
 *          properties:
 *            id:
 *              description: ID of the RegattaResultTable
 *              type: string
 *        - $ref: '#/definitions/RegattaResultTableParams'
 *
 *  @swagger
 *  definitions:
 *    RegattaResultTableUpdateParams:
 *      allOf:
 *        - $ref: '#/definitions/RegattaResultTableQueryParams'
 *        - type: object
 *          required:
 *            - id
 *
 *  @swagger
 *  definitions:
 *    RegattaResultTable:
 *      allOf:
 *        - $ref: '#/definitions/RegattaResultTableCreateParams'
 *        - $ref: '#/definitions/RegattaResultTableUpdateParams'
 *        - $ref: '#/definitions/Timestamps'
 *
 *  @swagger
 *  components:
 *    schemas:
 *      RegattaResultTable:
 *        allOf:
 *          - $ref: '#/definitions/RegattaResultTable'
 *          - type: object
 *            properties:
 *              Regatta:
 *                description: Regatta of the RegattaResultTable
 *                $ref: '#/components/schemas/Regatta'
 *              RegattaGroup:
 *                description: RegattaGroup of the RegattaResultTable
 *                $ref: '#/components/schemas/RegattaGroup'
 *              RegattaResultTableRaces:
 *                description: RegattaResultTableRaces of the RegattaResultTable
 *                type: array
 *                items:
 *                  $ref: '#/components/schemas/RegattaResultTableRace'
 *              RegattaResultTableEntries:
 *                description: RegattaResultTableEntries of the RegattaResultTable
 *                type: array
 *                items:
 *                  $ref: '#/components/schemas/RegattaResultTableEntry'
 */

// Enums
import {
  RegattaResultTableType,
} from '../enums';

// Types
import type {
  $Model,
  $ModelClass,
  $ModelClasses,
} from '.';
import type {
  $Field,
} from '../types';

export type $RelationIds = {
  RegattaGroupId: string;
  RegattaId: string;
};

export type $Params = {
  Discards: number;
  Name: string;
  StartGroup: null | number;
  Type: RegattaResultTableType;
};

export type $CreateParams = $Params & $RelationIds;

export type $UpdateParams = {
  id: string;
} & Partial<$CreateParams>;

export type $UpsertParams = $CreateParams & {
  id?: string;
};

export type $QueryParams = {
  id?: null | string;
} & Partial<$CreateParams>;

export type $Associations = {
  Regatta?: $Model<'Regatta'>;
  RegattaGroup?: $Model<'RegattaGroup'>;
  RegattaResultTableEntries?: Array<$Model<'RegattaResultTableEntry'>>;
  RegattaResultTableRaces?: Array<$Model<'RegattaResultTableRace'>>;
};

export type $ModelParams = $Associations & $CreateParams & {
  id: string;
};

type $Fields = $Params & {
  id: string;
};

const fields = (
  Sequelize,
): Record<keyof $Fields, $Field> => ({
  Discards: {
    allowNull: false,
    type: Sequelize.INTEGER.UNSIGNED,
  },
  id: {
    allowNull: false,
    defaultValue: Sequelize.UUIDV4,
    primaryKey: true,
    type: Sequelize.UUID,
    unique: true,
  },
  Name: {
    allowNull: false,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  StartGroup: {
    allowNull: true,
    type: Sequelize.INTEGER.UNSIGNED,
  },
  Type: {
    allowNull: false,
    type: Sequelize.ENUM(...Object.values(RegattaResultTableType)),
    validate: {
      notEmpty: true,
    },
  },
});

export default ((
  Sequelize,
  stack,
): $ModelClass<'RegattaResultTable'> => {
  const RegattaResultTable = stack.define(
    'RegattaResultTable',
    fields(Sequelize),
    {
      tableName: 'RegattaResultTables',
    },
  );

  RegattaResultTable.associate = (models: $ModelClasses) => {
    models.RegattaResultTable.belongsTo(
      models.Regatta,
      {
        as: 'Regatta',
        foreignKey: 'RegattaId',
      },
    );
    models.RegattaResultTable.belongsTo(
      models.RegattaGroup,
      {
        as: 'RegattaGroup',
        foreignKey: 'RegattaGroupId',
      },
    );
    models.RegattaResultTable.hasMany(
      models.RegattaResultTableRace,
      {
        as: 'RegattaResultTableRaces',
        foreignKey: 'RegattaResultTableId',
      },
    );
    models.RegattaResultTable.hasMany(
      models.RegattaResultTableEntry,
      {
        as: 'RegattaResultTableEntries',
        foreignKey: 'RegattaResultTableId',
      },
    );
    models.RegattaResultTable.hasMany(
      models.RegattaProtestEnd,
      {
        as: 'RegattaProtestEnds',
        foreignKey: 'RegattaResultTableId',
      },
    );
  };

  RegattaResultTable.addScope(
    'defaultScope',
    {
      order: [['createdAt', 'ASC']],
    },
  );

  return RegattaResultTable;
});
